import React, {
  useContext, useState, useEffect, useMemo,
} from 'react';
import PropTypes from 'prop-types';
import { Modal, Box, CircularProgress } from '@mui/material';
// eslint-disable-next-line import/no-extraneous-dependencies
import { X } from 'lucide-react';
import { sendRequest } from '../../api/shootAPI';
import API from '../../api/endpoints';
import { NotificationContext } from '../../contexts/Notification';
import { UserContext } from '../../contexts/User';
import { ChipSelectSearch } from '../../components/muiCustomized/ChipSelectSearchable';

function EditTeamModal({
  closeModal,
  onTeamEdited,
  name,
  managers,
  members,
  teamId,
}) {
  const [formData, setFormData] = useState({
    name, managers, members,
  });
  const [workspaceMembers, setWorkspaceMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const { displayNotification } = useContext(NotificationContext);
  const { activeWorkspace } = useContext(UserContext);

  const handleEditTeam = async () => {
    setIsUpdating(true);
    const { success } = await sendRequest({
      method: 'put',
      url: API.WORKSPACE.updateTeam(activeWorkspace.id, teamId),
      data: formData,
    });
    if (success) {
      setTimeout(() => {
        onTeamEdited();
        closeModal();
        displayNotification({
          message: `Changes to “${formData.name}” saved.`,
          severity: 'success',
          autoHideDuration: 3000,
        });
      }, 500);
    } else {
      displayNotification({
        message: 'There was an issue updating team.',
        severity: 'error',
      });
    }
    setIsUpdating(false);
  };

  const handleFormChanged = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectAllManagers = () => {
    setFormData({
      ...formData,
      managers: workspaceMembers.filter((member) => !formData.members.some((m) => m.id === member.id)),
    });
  };

  const handleSelectAllMembers = () => {
    setFormData({
      ...formData,
      members: workspaceMembers.filter((member) => !formData.managers.some((m) => m.id === member.id)),
    });
  };

  const fetchMembers = async () => {
    setIsLoading(true);
    const { success, data } = await sendRequest({
      method: 'get',
      url: API.WORKSPACE.members(activeWorkspace.id),
      params: {
        pageSize: 0,
      },
    });
    if (success) {
      const filteredMembers = data.members
        .filter(({ haveJoined }) => haveJoined)
        .map((item) => ({
          id: item.id,
          name: `${item.user.firstName} ${item.user.lastName}`,
        }));
      filteredMembers.sort((item1, item2) => {
        if (item1.name > item2.name) return 1;
        if (item1.name < item2.name) return -1;
        return 0;
      });
      setWorkspaceMembers(filteredMembers);
    } else {
      displayNotification({
        message: 'There was an issue fetching members in this workspace. Please try again',
        severity: 'error',
      });
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isChanged = useMemo(() => {
    const isManagerRemoved = managers.some((manager) => !formData.managers.some((m) => m.id === manager.id));
    const isMemberRemoved = members.some((member) => !formData.members.some((m) => m.id === member.id));
    return name !== formData.name
        || (isManagerRemoved || managers.length !== formData.managers.length)
        || (isMemberRemoved || members.length !== formData.members.length);
  }, [formData, managers, members, name]);

  return (
    <Modal
      open
      onClose={closeModal}
      className="flex-row align-c jstfy-c create-team-modal"
      // Rendered inside the clickable TeamCard — portal events bubble up the
      // React tree, so without this every click in the modal also clicks the card.
      onClick={(e) => e.stopPropagation()}
    >
      <Box style={{
        width: 448,
        padding: 24,
        background: '#fff',
        borderRadius: 8,
        maxHeight: 768,
        overflowY: 'auto',
        position: 'relative',
      }}
      >
        <button
          type="button"
          aria-label="Close"
          onClick={closeModal}
          className={'absolute top-4 right-4 bg-transparent border-0 p-0 text-ink-500 '
            + 'opacity-70 hover:opacity-100 transition-opacity'}
        >
          <X size={16} />
        </button>
        <h5 className="text-lg font-semibold text-ink-900 mb-4">Edit Team</h5>
        {isLoading && (
          <div className="position-relative" style={{ minHeight: 200 }}>
            <div className="edit-modal loader-container">
              <CircularProgress />
            </div>
          </div>
        )}
        {!isLoading && (
          <div className="space-y-4">
            <div>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label htmlFor="edit-team-name" className="block text-sm font-medium text-ink-900 mb-1.5">
                Edit Team Name
              </label>
              <input
                id="edit-team-name"
                name="name"
                type="text"
                value={formData.name}
                onChange={handleFormChanged}
                className={'w-full px-3 py-2 border border-rule rounded-[8px] text-sm bg-white '
                  + 'text-ink-900 placeholder:text-ink-500 focus:outline-none focus:ring-2 '
                  + 'focus:ring-brand-500/20 focus:border-brand-500 transition'}
              />
            </div>
            <ChipSelectSearch
              redesign
              id="managers"
              label="Edit Manager"
              placeholder="Required"
              selectAllBtn
              selectedMembers={
                  workspaceMembers.filter((member) => formData.managers.some((m) => m.id === member.id))
              }
              handleChange={handleFormChanged}
              handleSelectAll={handleSelectAllManagers}
              members={
                  workspaceMembers.filter((member) => !formData.members.some((m) => m.id === member.id))
              }
            />
            <ChipSelectSearch
              redesign
              id="members"
              label="Edit Team Members"
              placeholder="Required"
              selectAllBtn
              count={`${formData.members.length}/${workspaceMembers.length}`}
              selectedMembers={
                  workspaceMembers.filter((member) => formData.members.some((m) => m.id === member.id))
              }
              handleSelectAll={handleSelectAllMembers}
              handleChange={handleFormChanged}
              members={
                  workspaceMembers.filter((member) => !formData.managers.some((m) => m.id === member.id))
              }
            />
          </div>
        )}
        <div className="flex items-center justify-start gap-2 mt-4">
          <button
            type="button"
            onClick={closeModal}
            className={'inline-flex items-center justify-center px-4 py-2 border border-rule '
              + 'rounded-[8px] text-sm font-medium text-ink-900 bg-white hover:bg-gray-50 transition'}
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!isChanged
              || isUpdating
              || !(formData.name && formData.managers.length && formData.members.length)}
            onClick={handleEditTeam}
            className={'inline-flex items-center justify-center px-4 py-2 bg-[#53A551] text-white '
              + 'rounded-[8px] text-sm font-medium hover:bg-[#458a44] disabled:bg-[#53A551]/40 '
              + 'disabled:cursor-not-allowed transition'}
          >
            {isUpdating ? 'Updating...' : 'Save Changes'}
          </button>
        </div>
      </Box>
    </Modal>
  );
}

EditTeamModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  onTeamEdited: PropTypes.func.isRequired,
  name: PropTypes.string.isRequired,
  managers: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
  }).isRequired),
  members: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
  }).isRequired),
  teamId: PropTypes.number.isRequired,
};

export default EditTeamModal;
